import { BarDatum, BarSvgProps, ResponsiveBar } from '@nivo/bar'
import { useState } from 'react'
import { COMPLEXITY_COMPOUND_METRIC_RATIO, COMPLEXITY_METRIC_RATIO } from '../../../../model/caf.interface'
import { GRAPH_LABEL_TEXT_COLOR, GRAPH_TOOLTIP_CLS, graph_common_props } from '../../../../modules/consts/caf'
import { axisRange, maxAxisRange } from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import CommonLegendList, { LEGEND_SHAPE } from './common/CommonLegendList'

import { AxisTickProps } from '@nivo/axes'
import { DSTooltip } from '@repo/design-system/index'
import Image from 'next/image'
import { useCafScoresV5 } from '../../../../modules/apis/webview/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ICON_QUESTION_GRAY700 } from '../../../../modules/svg/svg'
import { COLORS } from '../../../../modules/vars'
import { useWebviewModal } from '../common/Modal/WebviewModal'

interface IComplexityVocabRangeGraphProps {
  data: any
  indexBy: string
  title: string
}

interface IGraphData extends BarDatum {
  top25_score: number
  native_score: number
  my_score: number
  difficulty?: string
  type?: string
  tooltip_label: string
  top25_score_color: string
  my_score_color: string
  native_score_color: string
}

const CHART_KEY = ['my_score', 'top25_score', 'native_score']

const CHART_STYLE: Partial<BarSvgProps<BarDatum>> = {
  ariaLabel: 'bar-chart',
  role: 'application',
  enableLabel: false,
  groupMode: 'grouped',
  theme: graph_common_props,
  margin: { bottom: 30, left: 30, top: 0 },
  padding: 0.3,
  innerPadding: 2,
  borderRadius: 2,
  valueScale: { type: 'linear' },
  indexScale: { type: 'band', round: true },
  enableGridX: true,
  enableGridY: true,
}

const ComplexityVocabRangeGraph = ({ data, indexBy = 'difficulty', title }: IComplexityVocabRangeGraphProps) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const webviewPopup = useWebviewModal()
  const { data: scoreData, isLoading } = useCafScoresV5(
    indexBy === 'difficulty' ? 'vocab' : indexBy === 'type' ? 'sentence' : 'other'
  )
  const [targetBar, setTargetBar] = useState<{ index: number; color: string | number }>({ index: null, color: null })

  const labelList = [
    { label: t(n_caf.complexity.my_mtld), color: COLORS.PURPLE500, type: LEGEND_SHAPE.square },
    { label: t(n_caf.complexity.top25), color: COLORS.YELLOW500, type: LEGEND_SHAPE.square },
    { label: t(n_caf.complexity.native), color: COLORS.BLUE500, type: LEGEND_SHAPE.square },
  ]

  const graphData: IGraphData[] =
    scoreData?.top_25_ratio &&
    data?.map((dataItem, index) => {
      if (indexBy === 'difficulty') {
        return {
          my_score: dataItem.ratio,
          top25_score: scoreData.top_25_ratio[COMPLEXITY_METRIC_RATIO[dataItem.difficulty]],
          native_score: scoreData.native_ratio[COMPLEXITY_METRIC_RATIO[dataItem.difficulty]],
          difficulty: t(n_caf.complexity['graph_' + COMPLEXITY_METRIC_RATIO[dataItem.difficulty]]),
          tooltip_label: dataItem.difficulty,
          my_score_color:
            targetBar.color && (targetBar.color !== CHART_KEY[0] || targetBar.index !== index)
              ? COLORS.PURPLE50
              : COLORS.PURPLE500,
          top25_score_color:
            targetBar.color && (targetBar.color !== CHART_KEY[1] || targetBar.index !== index)
              ? COLORS.YELLOW50
              : COLORS.YELLOW500, // targetBar.index === index && targetBar.color === 'top25'

          native_score_color:
            targetBar.color && (targetBar.color !== CHART_KEY[2] || targetBar.index !== index)
              ? COLORS.BLUE50
              : COLORS.BLUE500,
        }
      } else {
        return {
          my_score: dataItem.ratio,
          top25_score: scoreData.top_25_ratio[COMPLEXITY_COMPOUND_METRIC_RATIO[dataItem.type]],
          native_score: scoreData.native_ratio[COMPLEXITY_COMPOUND_METRIC_RATIO[dataItem.type]],
          type: t(n_caf.complexity['graph_' + COMPLEXITY_COMPOUND_METRIC_RATIO[dataItem.type]]),
          tooltip_label: dataItem.type,
          my_score_color:
            targetBar.color && (targetBar.color !== CHART_KEY[0] || targetBar.index !== index)
              ? COLORS.PURPLE50
              : COLORS.PURPLE500,
          top25_score_color:
            targetBar.color && (targetBar.color !== CHART_KEY[1] || targetBar.index !== index)
              ? COLORS.YELLOW50
              : COLORS.YELLOW500, // targetBar.index === index && targetBar.color === 'top25'

          native_score_color:
            targetBar.color && (targetBar.color !== CHART_KEY[2] || targetBar.index !== index)
              ? COLORS.BLUE50
              : COLORS.BLUE500,
        }
      }
    })
  const yRange = axisRange(
    graphData?.length && graphData[0].native_score ? maxAxisRange(graphData, CHART_KEY[2]) : 0,
    20,
    3
  )

  const handleClickBarItem = (e) => {
    const findIndex =
      indexBy === 'difficulty'
        ? graphData.findIndex((item) => item.difficulty === e.data.difficulty)
        : graphData.findIndex((item) => item.type === e.data.type)
    if (findIndex >= 0) {
      setTargetBar({ index: findIndex, color: e.id })
    }
  }

  const goToDetailPage = () => {
    if (isMobile) {
      webviewPopup.show({
        id: 'mtld_popup',
        title: indexBy === 'difficulty' ? t(n_caf.complexity.tab1) : t(n_caf.complexity.tab2),
        body: indexBy === 'difficulty' ? t(n_caf.vocablv_def.modal_body) : t(n_caf.sentence_def.modal_body),
      })
    }
  }
  console.log(graphData, 'graphData')
  if (!graphData || isLoading) {
    return <></>
  }

  if (isMobile) {
    return (
      <>
        <div className="no-scrollbar h-[212px] overflow-x-scroll">
          <ResponsiveBar
            {...CHART_STYLE}
            data={graphData}
            keys={CHART_KEY}
            indexBy={indexBy}
            colorBy="id"
            colors={({ id, data }) => String(data[`${id}_color`])}
            axisLeft={{
              tickSize: 0,
              tickRotation: 0,
              tickValues: yRange,
              renderTick: (tick: AxisTickProps<string>) => (
                <CustomTick tick={tick} color={GRAPH_LABEL_TEXT_COLOR} isMobile={isMobile} />
              ),
            }}
            axisBottom={{
              tickSize: 0,
              tickRotation: 0,
              legendPosition: 'start',
              legendOffset: 40,
            }}
            tooltip={ToolTip}
            onClick={handleClickBarItem}
            onMouseLeave={() => setTargetBar({ index: null, color: null })}
          />
        </div>
        <div className="flex items-center justify-center">
          <div className="text-h5 h-[18px]  text-center font-medium text-gray-500">{title}</div>
          <Image
            src={ICON_QUESTION_GRAY700}
            width={18}
            height={18}
            alt="question"
            className="ml-7"
            onClick={goToDetailPage}
          />
        </div>
        <CommonLegendList list={labelList} />
      </>
    )
  } else {
    return (
      <div className="no-scrollbar h-[311px] w-[424px] overflow-x-scroll rounded-[6px] border border-gray-200 bg-white p-[16px]">
        <div className="h-[216px] w-[388px]">
          <ResponsiveBar
            {...CHART_STYLE}
            theme={{
              ...graph_common_props,
              axis: {
                ticks: {
                  text: {
                    fontSize: '14px',
                  },
                },
              },
            }}
            data={graphData}
            keys={CHART_KEY}
            indexBy={indexBy}
            colorBy="id"
            colors={({ id, data }) => String(data[`${id}_color`])}
            axisLeft={{
              tickSize: 0,
              tickRotation: 0,
              tickValues: yRange,
              renderTick: (tick: AxisTickProps<string>) => (
                <CustomTick tick={tick} color={GRAPH_LABEL_TEXT_COLOR} isMobile={isMobile} />
              ),
            }}
            axisBottom={{
              tickSize: 0,
              tickRotation: 0,
              legendPosition: 'start',
              legendOffset: 40,
            }}
            tooltip={ToolTip}
            onClick={handleClickBarItem}
            onMouseLeave={() => setTargetBar({ index: null, color: null })}
          />
        </div>
        <div className="mt-[4px] flex items-center justify-center">
          <div className="text-h5 h-[18px] text-center font-medium text-gray-500">{title}</div>
          <DSTooltip
            place="bottom"
            isMultiline={true}
            isOpenOnClick={true}
            triggerComponent={
              <Image
                src={ICON_QUESTION_GRAY700}
                width={18}
                height={18}
                alt="question"
                className="ml-7"
                onClick={() => null}
              />
            }
            contentComponent={t(n_caf.vocablv_def.modal_body)}
          />
        </div>
        <CommonLegendList list={labelList} />
      </div>
    )
  }
}

const CustomTick = ({ tick, color = COLORS.GRAY500, isMobile }) => {
  return (
    <g transform={`translate(${tick.x - 15},${tick.y})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fill: color,
          fontSize: isMobile ? 10 : 12,
        }}
      >
        {tick.value}%
      </text>
    </g>
  )
}

const ToolTip = ({ value, data }) => {
  return (
    <div className={GRAPH_TOOLTIP_CLS}>
      <span className="text-gray-700">{data.tooltip_label}</span>
      <span className="pl-[8px] font-medium text-black">{value}%</span>
    </div>
  )
}

export default ComplexityVocabRangeGraph
